/**
 * Place of Performance Transform Utilities
 *
 * Pure, idempotent functions for normalizing Place of Performance (PoP) data.
 * These transforms handle:
 * - Location formatting (city, state, country)
 * - ZIP code with +4 extension formatting
 * - Congressional district with redistricting history
 * - Foreign vs domestic location detection
 */

// ============================================================================
// Types
// ============================================================================

/** Raw location data from API */
export interface RawLocationData {
  city?: string | null
  state?: string | null
  zip?: string | null
  zipLast4?: string | null
  county?: string | null
  country?: string | null
  congressionalDistrict?: string | null
  congressionalDistrictCurrent?: string | null
  foreignProvince?: string | null
  foreignDescription?: string | null
}

/** Normalized PoP data ready for display */
export interface NormalizedPlaceOfPerformance {
  /** Pre-formatted location string (city, state, country for foreign) */
  formatted: string | null
  /** City name */
  city: string | null
  /** State/province code */
  state: string | null
  /** 5-digit ZIP code */
  zip: string | null
  /** 4-digit ZIP extension */
  zipLast4: string | null
  /** Pre-formatted ZIP with +4 extension */
  zipFormatted: string | null
  /** County name */
  county: string | null
  /** Country code/name */
  country: string | null
  /** Original congressional district at time of award */
  congressionalDistrict: string | null
  /** Current congressional district after redistricting */
  congressionalDistrictCurrent: string | null
  /** Pre-formatted congressional district display with redistricting note */
  congressionalDistrictFormatted: string | null
  /** Whether CD has been redistricted (original != current) */
  hasRedistricted: boolean
  /** Foreign province name */
  foreignProvince: string | null
  /** Foreign location description */
  foreignDescription: string | null
  /** True if this is a non-US location */
  isForeign: boolean
}

// ============================================================================
// Pure Helper Functions
// ============================================================================

/**
 * Determine if a country code represents a foreign (non-US) location.
 * Pure function - same input always produces same output.
 *
 * @param country - Country code or name
 * @returns true if the location is outside the US
 */
export function isForeignLocation(country: string | null | undefined): boolean {
  if (!country) return false
  const normalized = country.toUpperCase()
  return (
    normalized !== 'USA' &&
    normalized !== 'US' &&
    normalized !== 'UNITED STATES'
  )
}

/**
 * Format a ZIP code with optional +4 extension.
 * Pure function - same inputs always produce same output.
 *
 * @param zip - 5-digit ZIP code
 * @param zipLast4 - Optional 4-digit extension
 * @returns Formatted ZIP string (e.g., "12345" or "12345-6789")
 */
export function formatZipCode(
  zip: string | null | undefined,
  zipLast4: string | null | undefined,
): string | null {
  if (!zip) return null
  return zipLast4 ? `${zip}-${zipLast4}` : zip
}

/**
 * Format a congressional district with redistricting history.
 * Pure function - same inputs always produce same output.
 *
 * @param original - Congressional district at time of award
 * @param current - Current congressional district after redistricting
 * @returns Object with display string and whether redistricting occurred
 */
export function formatCongressionalDistrict(
  original: string | null | undefined,
  current: string | null | undefined,
): { formatted: string | null; hasRedistricted: boolean } {
  // Prefer current, fall back to original
  const displayDistrict = current || original

  if (!displayDistrict) {
    return { formatted: null, hasRedistricted: false }
  }

  // Check if redistricting occurred (both exist and differ)
  const hasRedistricted = !!(current && original && current !== original)

  return {
    formatted: displayDistrict,
    hasRedistricted,
  }
}

/**
 * Format a location into a single display string.
 * Pure function - same inputs always produce same output.
 *
 * @param location - Raw location data
 * @returns Formatted string (e.g., "Austin, TX" or "London, UK")
 */
export function formatLocationString(
  location: RawLocationData | null | undefined,
): string | null {
  if (!location) return null

  const parts: string[] = []
  if (location.city) parts.push(location.city)
  if (location.state) parts.push(location.state)

  // Include country only for foreign locations
  if (location.country && isForeignLocation(location.country)) {
    parts.push(location.country)
  }

  return parts.length > 0 ? parts.join(', ') : null
}

// ============================================================================
// Main Transform Function
// ============================================================================

/**
 * Normalize raw location data into display-ready PoP format.
 * Pure, idempotent function - same input always produces same output.
 *
 * This function pre-computes all display values so rendering components
 * can remain pure presentation components with no computation in JSX.
 *
 * @param location - Raw location data from API
 * @returns Normalized PoP data with all display values pre-computed
 */
export function normalizePlaceOfPerformance(
  location: RawLocationData | null | undefined,
): NormalizedPlaceOfPerformance {
  // Handle null/undefined location
  if (!location) {
    return {
      formatted: null,
      city: null,
      state: null,
      zip: null,
      zipLast4: null,
      zipFormatted: null,
      county: null,
      country: null,
      congressionalDistrict: null,
      congressionalDistrictCurrent: null,
      congressionalDistrictFormatted: null,
      hasRedistricted: false,
      foreignProvince: null,
      foreignDescription: null,
      isForeign: false,
    }
  }

  // Compute derived values
  const isForeign = isForeignLocation(location.country)
  const zipFormatted = formatZipCode(location.zip, location.zipLast4)
  const { formatted: congressionalDistrictFormatted, hasRedistricted } =
    formatCongressionalDistrict(
      location.congressionalDistrict,
      location.congressionalDistrictCurrent,
    )

  return {
    formatted: formatLocationString(location),
    city: location.city ?? null,
    state: location.state ?? null,
    zip: location.zip ?? null,
    zipLast4: location.zipLast4 ?? null,
    zipFormatted,
    county: location.county ?? null,
    country: location.country ?? null,
    congressionalDistrict: location.congressionalDistrict ?? null,
    congressionalDistrictCurrent: location.congressionalDistrictCurrent ?? null,
    congressionalDistrictFormatted,
    hasRedistricted,
    foreignProvince: location.foreignProvince ?? null,
    foreignDescription: location.foreignDescription ?? null,
    isForeign,
  }
}
